import { Injectable } from '@angular/core';

import { ICD10_DATA } from 'src/app/shared/models/icd10/icd10-data';
import { Icd10Dto } from 'src/app/shared/models/icd10/icd10-dto.model';

@Injectable({
  providedIn: 'root',
})
export class Icd10Service {
  private readonly codes: Icd10Dto[] = [];
  private isInitialized = false;

  /**
   * ICD-10-GM Diagnosen nach Code, Name oder Synonym durchsuchen.
   *
   * Die Suche ist case-insensitive und matcht sowohl auf den Code als auch
   * auf den Diagnose-Namen und alle Synonyme. Es werden nur Suchbegriffe mit
   * mindestens 3 Zeichen akzeptiert. Das Ergebnis ist auf maximal
   * 100 Treffer begrenzt.
   *
   * @param searchTerm - Suchbegriff (Code, Name oder Synonym)
   * @returns Array von passenden ICD10Dto-Objekten
   * @example
   * ```typescript
   * const results = await this.icd10Service.search('E11');
   * // [{ code: 'E11.9', name: 'Typ-2-Diabetes', ... }]
   * ```
   */
  public async search(searchTerm: string): Promise<Icd10Dto[]> {
    await this.ensureInitialized();

    if (!searchTerm || searchTerm.trim().length < 3) {
      return [];
    }

    const normalizedSearch = searchTerm.toLowerCase().trim();
    const results = this.codes.filter(code => {
      return (
        code.code.toLowerCase().includes(normalizedSearch) ||
        code.name.toLowerCase().includes(normalizedSearch) ||
        code.synonyms.some(syn => syn.toLowerCase().includes(normalizedSearch))
      );
    });

    const sortedResults = [...results].sort((a, b) => b.code.localeCompare(a.code));
    return sortedResults.slice(0, 100);
  }

  /**
   * Prüft ob ein ICD-10-Code in der Datenbank existiert.
   *
   * @param code - Zu prüfender ICD-10-Code
   * @returns true wenn der Code existiert
   */
  public async exists(code: string): Promise<boolean> {
    await this.ensureInitialized();
    return this.codes.some(c => c.code === code);
  }

  /**
   * Gibt einen einzelnen ICD-10-Code anhand seines Codes zurück.
   *
   * @param code - Der ICD-10-Code
   * @returns Das Icd10Dto-Objekt oder null wenn nicht gefunden
   */
  public async findByCode(code: string): Promise<Icd10Dto | null> {
    await this.ensureInitialized();
    const found = this.codes.find(c => c.code === code);
    return found ?? null;
  }

  private async ensureInitialized(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    const initialCodes = [...ICD10_DATA];
    this.codes.push(...initialCodes.map(code => new Icd10Dto(code)));

    this.isInitialized = true;
  }
}
